import type { SensorType } from "../../../../shared.js";
import { DirectSubChannel, SensorPortCommunicationMode } from "../../../portConfigs.js";
import { SensorClusterConfig, SensorElementOutput } from "../types.js";

// ─── Flomec QS200 series (PWM flowmeters) ────────────────────────────────────
//
// Calibration is specified as litersPerPulse (L/pulse) with no frequency offset.
// The flowmeter element uses kFactor (L/pulse) directly.

type FlomecParams = {
    litersPerPulse: number;
    minRawValueHz: number;
    modelName: string;
    friendlyName: string;
    friendlyShortName: string;
    legacySensorType: SensorType;
};

function flomecFlowmeterCluster({
    litersPerPulse,
    minRawValueHz,
    modelName,
    friendlyName,
    friendlyShortName,
    legacySensorType,
}: FlomecParams): SensorClusterConfig<Record<string, never>> {
    const sensorElements: SensorElementOutput<"flowmeter">[] = [
        {
            sensorElementConfigKey: "flowmeter",
            parameters: { kFactor: litersPerPulse, minimumFrequency: minRawValueHz, frequencyOffset: 0 },
            legalConvertedRange: [0, null],
            subChannel: DirectSubChannel.PULSE_FREQUENCY,
        },
    ];

    return {
        communicationMode: SensorPortCommunicationMode.DIRECT,
        requiredSubChannels: [DirectSubChannel.PULSE_FREQUENCY],
        optionalSubChannels: [],
        isPermanentlyAttached: false,
        parameterConfigs: {},
        manufacturerName: "Flomec",
        modelName,
        friendlyName,
        friendlyShortName,
        generateSensorElements: () => sensorElements,
        migrationProperties: {
            legacySensorType,
            legacyParametersToClusterParameters: () => ({}),
            clusterParametersToLegacyParameters: () => ({}),
        },
    };
}

export const flomecFlowmeterClusterConfigs: Record<string, SensorClusterConfig<Record<string, never>>> = {
    // QS200-10 (1" pipe)
    flomecFlowmeterOneInchSch40: flomecFlowmeterCluster({
        litersPerPulse: 0.0352,
        minRawValueHz: 0.3932965084,
        modelName: "QS200-10",
        friendlyName: `QS200 1" Sch 40 Flowmeter`,
        friendlyShortName: `1" Flow`,
        legacySensorType: "flomecFlowmeterOneInchSch40",
    }),
    flomecFlowmeterOneInchSch80: flomecFlowmeterCluster({
        litersPerPulse: 0.0338,
        minRawValueHz: 0.3932965084,
        modelName: "QS200-10",
        friendlyName: `QS200 1" Sch 80 Flowmeter`,
        friendlyShortName: `1" Flow`,
        legacySensorType: "flomecFlowmeterOneInchSch80",
    }),

    // QS200-15 (1 1/2" pipe)
    flomecFlowmeterOneAndAHalfInchSch40: flomecFlowmeterCluster({
        litersPerPulse: 0.05,
        minRawValueHz: 0.6935224901,
        modelName: "QS200-15",
        friendlyName: `QS200 1 1/2" Sch 40 Flowmeter`,
        friendlyShortName: `1 1/2" Flow`,
        legacySensorType: "flomecFlowmeterOneAndAHalfInchSch40",
    }),
    flomecFlowmeterOneAndAHalfInchSch80: flomecFlowmeterCluster({
        litersPerPulse: 0.0496,
        minRawValueHz: 0.6935224901,
        modelName: "QS200-15",
        friendlyName: `QS200 1 1/2" Sch 80 Flowmeter`,
        friendlyShortName: `1 1/2" Flow`,
        legacySensorType: "flomecFlowmeterOneAndAHalfInchSch80",
    }),

    // QS200-20 (2" pipe)
    flomecFlowmeterTwoInchSch40: flomecFlowmeterCluster({
        litersPerPulse: 0.0922,
        minRawValueHz: 0.6292393855,
        modelName: "QS200-20",
        friendlyName: `QS200 2" Sch 40 Flowmeter`,
        friendlyShortName: `2" Flow`,
        legacySensorType: "flomecFlowmeterTwoInchSch40",
    }),
    flomecFlowmeterTwoInchSch80: flomecFlowmeterCluster({
        litersPerPulse: 0.0919,
        minRawValueHz: 0.6292393855,
        modelName: "QS200-20",
        friendlyName: `QS200 2" Sch 80 Flowmeter`,
        friendlyShortName: `2" Flow`,
        legacySensorType: "flomecFlowmeterTwoInchSch80",
    }),

    // QS200-30 (3" pipe)
    flomecFlowmeterThreeInchSch40: flomecFlowmeterCluster({
        litersPerPulse: 0.2614,
        minRawValueHz: 0.4973430543,
        modelName: "QS200-30",
        friendlyName: `QS200 3" Sch 40 Flowmeter`,
        friendlyShortName: `3" Flow`,
        legacySensorType: "flomecFlowmeterThreeInchSch40",
    }),
    flomecFlowmeterThreeInchSch80: flomecFlowmeterCluster({
        litersPerPulse: 0.2496,
        minRawValueHz: 0.4973430543,
        modelName: "QS200-30",
        friendlyName: `QS200 3" Sch 80 Flowmeter`,
        friendlyShortName: `3" Flow`,
        legacySensorType: "flomecFlowmeterThreeInchSch80",
    }),

    // QS200-40 (4" pipe)
    flomecFlowmeterFourInchSch40: flomecFlowmeterCluster({
        litersPerPulse: 0.4753,
        minRawValueHz: 0.475180049,
        modelName: "QS200-40",
        friendlyName: `QS200 4" Sch 40 Flowmeter`,
        friendlyShortName: `4" Flow`,
        legacySensorType: "flomecFlowmeterFourInchSch40",
    }),
    flomecFlowmeterFourInchSch80: flomecFlowmeterCluster({
        litersPerPulse: 0.4627,
        minRawValueHz: 0.475180049,
        modelName: "QS200-40",
        friendlyName: `QS200 4" Sch 80 Flowmeter`,
        friendlyShortName: `4" Flow`,
        legacySensorType: "flomecFlowmeterFourInchSch80",
    }),
};
